/**
 * downloadCoa
 *
 * Calls the backend Certificate of Attendance PDF endpoints and triggers a browser download.
 * Mirrors the printable-reports pattern (EP-12) since CoA reuses the same PDF generation +
 * S3/SHA3 caching infrastructure per EP-13.
 *
 * The backend exposes two distinct download endpoints with different auth (EP-13):
 *   - Organiser:   GET /api/v1/events/{eventId}/participants/{participantId}/certificate?lang={lang}
 *                  Authenticated with the organiser's Keycloak JWT (see {@link getApiToken}).
 *   - Participant: GET /api/v1/event/participant/certificate?lang={lang}
 *                  Authenticated with the participant's magic-link token (`?tk=`), sent as a
 *                  Bearer token — the participant has no Keycloak session.
 *
 * SHA3 hashing / S3 caching is handled entirely by the backend — the frontend has no
 * awareness of cache keys.
 */

import { getApiToken } from '@proaxiv/server-api'
import { normaliseLanguage, type ReportLanguage } from './downloadParticipantReport'

export type { ReportLanguage as CoaLanguage }
export { normaliseLanguage }

interface DownloadCoaParams {
  eventId: string
  participantId: string
  /** Used to build a human-friendly download filename. */
  participantName?: string
  language: ReportLanguage
}

/**
 * Triggers a browser download of a participant's Certificate of Attendance PDF, as the event
 * organiser. Used from the Attended Participants table's "CoA last sent" cell.
 *
 * @throws  Error with a human-readable message on non-2xx responses.
 */
export async function downloadCertificateOfAttendance({
  eventId,
  participantId,
  participantName,
  language,
}: DownloadCoaParams): Promise<void> {
  const token = getApiToken()
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
  const res = await fetch(
    `/api/v1/events/${eventId}/participants/${participantId}/certificate?lang=${language}`,
    { headers }
  )
  if (!res.ok) {
    throw new Error(`Certificate download failed (HTTP ${res.status})`)
  }
  const blob = await res.blob()
  triggerDownload(blob, buildFileName(participantName, language))
}

interface DownloadOwnCoaParams {
  /** The participant's magic-link token (the `?tk=` query param), sent as a Bearer token. */
  participantToken: string
  /** Used to build a human-friendly download filename. */
  participantName?: string
  language: ReportLanguage
}

/**
 * Triggers a browser download of the caller's own Certificate of Attendance PDF, as the
 * participant themselves. Used from the participant's ticket page.
 *
 * @throws  Error with a human-readable message on non-2xx responses.
 */
export async function downloadOwnCertificateOfAttendance({
  participantToken,
  participantName,
  language,
}: DownloadOwnCoaParams): Promise<void> {
  const res = await fetch(`/api/v1/event/participant/certificate?lang=${language}`, {
    headers: { Authorization: `Bearer ${participantToken}` },
  })
  if (!res.ok) {
    throw new Error(`Certificate download failed (HTTP ${res.status})`)
  }
  const blob = await res.blob()
  triggerDownload(blob, buildFileName(participantName, language))
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildFileName(participantName: string | undefined, language: ReportLanguage): string {
  const slug = (participantName ?? 'participant').trim().replace(/\s+/g, '_').toLowerCase()
  return `certificate_of_attendance_${slug}_${language}.pdf`
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
